import { useTheme } from '@mui/material/styles';

import { Chart, type ChartTableColumn } from './Chart';
import {
  baseChartOptions,
  categoryAxis,
  chartPalette,
  useChartScheme,
  valueAxis,
} from './chartTheme';
import { capToLimit, foldToOther, type ChartSeries } from './series';

/**
 * How to handle more series than the palette has slots. `sum` folds the remainder into one "Other" line
 * and is only correct when the series share a unit; `cap` plots the largest few and leaves the rest out
 * of the plot, which is what money series need because amounts in different currencies cannot be added.
 */
export type AreaTrendFold =
  | { readonly kind: 'sum'; readonly formatLabel: (value: number) => string }
  | { readonly kind: 'cap' };

export interface AreaTrendChartProps {
  readonly ariaLabel: string;
  readonly categories: readonly string[];
  readonly fold: AreaTrendFold;
  /**
   * Formats a plotted number for the axis ticks. Required: ECharts would otherwise print the bare float,
   * putting a number on screen that never passed through the caller's formatter.
   */
  readonly formatValue: (value: number) => string;
  readonly rowHeader: string;
  readonly series: readonly ChartSeries[];
  readonly tableCaption: string;
}

export function AreaTrendChart({
  ariaLabel,
  categories,
  fold,
  formatValue,
  rowHeader,
  series,
  tableCaption,
}: AreaTrendChartProps) {
  const theme = useTheme();
  const scheme = useChartScheme();
  const palette = chartPalette(theme, scheme);
  const base = baseChartOptions(theme, scheme);
  const visible =
    fold.kind === 'sum'
      ? foldToOther(series, palette.series.length, fold.formatLabel)
      : capToLimit(series, palette.series.length);
  // Under `sum` the table mirrors the plot, "Other" included, so the two tell the same story. Under
  // `cap` the plot omits the smallest series but the table still lists every one of them — capping is a
  // limit on how many lines are legible, never a reason to withhold a figure from a screen-reader user.
  const tableSeries = fold.kind === 'sum' ? visible : series;
  const columns: readonly ChartTableColumn[] = tableSeries.map((entry) => ({
    header: entry.name,
    values: entry.labels,
  }));

  return (
    <Chart
      ariaLabel={ariaLabel}
      columns={columns}
      option={{
        ...base,
        // A legend appears for two or more series; a single series is named by the card title.
        legend:
          visible.length > 1
            ? { ...base.legend, data: visible.map((entry) => entry.name) }
            : { show: false },
        series: visible.map((entry, index) => ({
          areaStyle: index === 0 ? { color: palette.areaFill } : { opacity: 0 },
          data: [...entry.values],
          emphasis: { focus: 'series' as const },
          lineStyle: { cap: 'round' as const, join: 'round' as const, width: 2 },
          name: entry.name,
          showSymbol: false,
          smooth: false,
          symbolSize: 8,
          type: 'line' as const,
        })),
        tooltip: {
          ...base.tooltip,
          // The crosshair finds the X so the reader aims at a date, not at a 2px line.
          axisPointer: { lineStyle: { color: palette.grid }, type: 'line' as const },
          // Each row reads its series' own formatted label for this category, so a currency line shows
          // its own currency rather than a bare float — and two currencies never look interchangeable.
          formatter: (params: readonly { axisValueLabel: string; seriesName: string }[]) => {
            const rows = params.map((entry) => {
              const match = visible.find((candidate) => candidate.name === entry.seriesName);
              const index = categories.indexOf(entry.axisValueLabel);
              return `${entry.seriesName}: ${match?.labels[index] ?? ''}`;
            });
            return [params[0]?.axisValueLabel ?? '', ...rows].join('<br/>');
          },
          trigger: 'axis' as const,
        },
        xAxis: { ...categoryAxis(theme, scheme), boundaryGap: false, data: [...categories] },
        yAxis: valueAxis(theme, scheme, formatValue),
      }}
      rowHeader={rowHeader}
      rows={categories}
      tableCaption={tableCaption}
    />
  );
}
